import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Image, Plus, Trash2, GripVertical, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import FileDropzone, { IMAGE_UPLOAD } from './FileDropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const createId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `poster_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};
const deleteUploadedFile = async (url) => {
  if (!url?.startsWith('/api/uploads/')) return;
  const filename = url.split('/').pop();
  if (filename) await axios.delete(`${API}/uploads/${encodeURIComponent(filename)}`);
};

const PostersEditor = ({ setProfile }) => {
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const saveTimer = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/profile`);
        setPosters(res.data.posters || []);
      } catch { toast.error('Failed to load posters'); }
      finally { setLoading(false); }
    })();
  }, []);

  const persistPosters = useCallback(async (updated) => {
    const res = await axios.put(`${API}/profile`, { posters: updated });
    setProfile?.(res.data);
    return res.data;
  }, [setProfile]);

  const savePosters = useCallback((updated) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await persistPosters(updated);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to save posters');
      }
    }, 600);
  }, [persistPosters]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const poster = {
        id: createId(),
        image_url: res.data.url,
        title: '',
        order: posters.length,
      };
      const updated = [...posters, poster];
      await persistPosters(updated);
      setPosters(updated);
      toast.success('Poster added');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDroppedPoster = async (upload) => {
    const poster = {
      id: createId(),
      image_url: upload.url,
      title: '',
      order: posters.length,
    };
    const updated = [...posters, poster];
    try {
      await persistPosters(updated);
      setPosters(updated);
    } catch (error) {
      try {
        await deleteUploadedFile(upload.url);
      } catch {
        // The original persistence error is more useful to the user.
      }
      throw error;
    }
  };

  const removePoster = async (id) => {
    const removed = posters.find(p => p.id === id);
    const updated = posters.filter(p => p.id !== id);
    try {
      await persistPosters(updated);
      setPosters(updated);
      await deleteUploadedFile(removed?.image_url);
      toast.success('Poster removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove poster');
    }
  };

  const updateTitle = (id, title) => {
    const updated = posters.map(p => p.id === id ? { ...p, title } : p);
    setPosters(updated);
    savePosters(updated);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(posters);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    const updated = items.map((p, i) => ({ ...p, order: i }));
    setPosters(updated);
    savePosters(updated);
  };

  if (loading) {
    return <div className="max-w-2xl flex items-center justify-center py-16">
      <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>;
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="posters-editor">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit font-bold text-2xl text-white">Posters</h1>
          <p className="text-[#A1A1AA] mt-1">Upload promotional banners, flyers, or event posters</p>
        </div>
        <div>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          {posters.length < 10 && (
            <Button data-testid="add-poster-btn" onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-violet-600 hover:bg-violet-700 text-white gap-2">
              {uploading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              Add Poster
            </Button>
          )}
        </div>
      </div>

      <p className="text-sm text-zinc-500">{posters.length}/10 posters</p>

      {posters.length < 10 && (
        <FileDropzone
          {...IMAGE_UPLOAD}
          testId="poster-file-dropzone"
          label="Choose a poster image or drag and drop"
          description={`JPG, PNG, GIF, WebP or HEIC - ${10 - posters.length} slots remaining`}
          disabled={uploading}
          onUploaded={handleDroppedPoster}
          successMessage="Poster added"
        />
      )}

      {posters.length === 0 ? (
        <div className="rounded-2xl bg-[#121212] border border-[#27272A] p-12 text-center" data-testid="posters-empty">
          <Image className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 font-medium">No posters yet</p>
          <p className="text-zinc-600 text-sm mt-1">Upload images to create a swipeable poster carousel on your profile</p>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="posters-list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
                {posters.map((poster, index) => (
                  <Draggable key={poster.id} draggableId={poster.id} index={index}>
                    {(provided, snapshot) => (
                      <div ref={provided.innerRef} {...provided.draggableProps}
                        className={`rounded-2xl bg-[#121212] border p-4 transition-all ${
                          snapshot.isDragging ? 'border-violet-500 shadow-lg shadow-violet-500/10' : 'border-[#27272A]'
                        }`}
                        data-testid={`poster-item-${index}`}>
                        <div className="flex items-start gap-3">
                          <div {...provided.dragHandleProps} className="mt-1 cursor-grab text-zinc-600 hover:text-zinc-400">
                            <GripVertical className="w-5 h-5" />
                          </div>

                          <div className="w-24 h-24 rounded-lg overflow-hidden bg-zinc-900 flex-shrink-0">
                            <img src={resolveMediaUrl(poster.image_url)}
                              alt={poster.title || 'Poster'} className="w-full h-full object-cover" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <Input value={poster.title || ''}
                              onChange={e => updateTitle(poster.id, e.target.value)}
                              className="bg-transparent border-transparent hover:border-[#27272A] focus:border-violet-500/50 text-white text-sm font-medium p-0 h-auto"
                              placeholder="Poster title (optional)" />
                            <p className="text-[10px] text-zinc-600 mt-2 truncate">{poster.image_url}</p>
                          </div>

                          <button data-testid={`delete-poster-${index}`}
                            onClick={() => removePoster(poster.id)}
                            className="text-zinc-600 hover:text-red-400 transition-colors mt-1">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default PostersEditor;
